import { useEffect, useMemo, useState } from 'react'
import { Bookmark, CalendarDays, Check, CircleCheck, Clapperboard, Clock3, Download, ExternalLink, FileJson, ImagePlus, LoaderCircle, MonitorPlay, Pause, Play, RadioTower, RotateCcw, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ArtworkSelection, ContentTag, ContentType, Film, LibraryAvailability, MediaMetadata, MetadataEnrichmentState, Quality } from '../lib/api'
import { formatBytes, formatDuration } from '../lib/api'
import { mediaSources, safeExternalHttpUrl, sourceForQuality } from '../lib/media'
import { ContentTypeSelect } from './ContentTypeSelect'
import { ArtworkWizard } from './ArtworkWizard'
import { ModalPortal } from './ModalPortal'
import { SenderLogo } from './SenderLogo'

interface Props {
  film: Film
  stateUpdating?: boolean
  stateError?: boolean
  classificationUpdating?: boolean
  classificationError?: boolean
  onClassificationChange: (contentType: ContentType, tags: ContentTag[]) => void
  onClose: () => void
  onDownload: () => void
  downloadDisabled?: boolean
  metadata?: MediaMetadata | null
  metadataEnrichment?: MetadataEnrichmentState | null
  metadataRetrying?: boolean
  metadataRetryError?: boolean
  onMetadataRetry?: () => void
  libraryStatus?: LibraryAvailability
  artworkEventId?: number
  artworkUrl?: string
  artworkError?: string
  onArtworkUrl?: (selection: ArtworkSelection) => Promise<void>
  onArtworkUpload?: (file: File) => Promise<void>
  onArtworkRemove?: () => Promise<void>
  onSubscribe?: () => void
  onBookmark?: () => void
  onSeen?: () => void
}

export function FilmDetailsDialog({ film, stateUpdating = false, stateError = false,
  classificationUpdating = false, classificationError = false, onClassificationChange,
  onClose, onDownload, downloadDisabled = false, metadata = null, artworkUrl,
  metadataEnrichment = null, metadataRetrying = false, metadataRetryError = false, onMetadataRetry,
  libraryStatus, artworkEventId,
  artworkError, onArtworkUrl, onArtworkUpload, onArtworkRemove,
  onSubscribe, onBookmark, onSeen }: Props) {
  const { t } = useTranslation()
  const sources = useMemo(() => mediaSources(film), [film])
  const [quality, setQuality] = useState<Quality>(() => sources.some((source) => source.quality === 'HIGH') ? 'HIGH' : 'NORMAL')
  const [playerOpen, setPlayerOpen] = useState(false)
  const [playerFailed, setPlayerFailed] = useState(false)
  const [artworkEditorOpen, setArtworkEditorOpen] = useState(false)
  const [artworkFormat, setArtworkFormat] = useState<'landscape' | 'poster' | 'square'>('landscape')
  const websiteUrl = safeExternalHttpUrl(film.websiteUrl)
  const selectedMediaUrl = safeExternalHttpUrl(sourceForQuality(film, quality))
  const playable = Boolean(selectedMediaUrl && isBrowserPlayable(selectedMediaUrl))
  const metadataUrl = safeExternalHttpUrl(metadata?.providerUrl ?? '')
  const metadataProviderLabel = metadata?.provider === 'SENDERSEITE'
    ? t('newMovies.broadcasterPage')
    : metadata?.provider === 'HEURISTIC'
      ? t('newMovies.heuristicMetadata')
      : metadata?.provider === 'WIKIDATA' ? 'Wikidata' : metadata?.provider
  const artworkSourceUrl = safeExternalHttpUrl(metadata?.artworkSourceUrl ?? '')
  const artworkLicenseUrl = safeExternalHttpUrl(metadata?.artworkLicenseUrl ?? '')
  const imdbUrl = metadata?.imdbId?.match(/^tt\d+$/) ? `https://www.imdb.com/title/${metadata.imdbId}/` : null
  const artworkEditable = Boolean(artworkEventId && onArtworkUrl && onArtworkUpload)
  const artworkSearchQuery = `${film.title}${metadata?.releaseYear ? ` ${metadata.releaseYear}` : ''}`
  const production = [metadata?.countries.join(', ') ?? '', metadata?.releaseYear?.toString() ?? '']
    .filter(Boolean).join(' · ')
  const libraryProfiles = libraryStatus?.copies.map((copy) => copy.downloadProfileName || copy.mediaRootLabel)
    .filter((value, index, values) => values.indexOf(value) === index) ?? []
  const downloadTitle = libraryStatus?.downloaded
    ? t('sendungen.downloadedAction', { profiles: libraryProfiles.join(', ') })
    : t('sendungen.download')
  const enrichmentLabel = metadataEnrichment?.status === 'MATCHED'
    ? t(metadata?.artworkAvailable ? 'newMovies.metadataStates.MATCHED_WITH_ARTWORK' : 'newMovies.metadataStates.MATCHED_WITHOUT_ARTWORK')
    : metadataEnrichment ? t(`newMovies.metadataStates.${metadataEnrichment.status}`) : ''

  useEffect(() => {
    setQuality(sources.some((source) => source.quality === 'HIGH') ? 'HIGH' : 'NORMAL')
    setPlayerOpen(false)
    setPlayerFailed(false)
    setArtworkEditorOpen(false)
    setArtworkFormat('landscape')
  }, [film.id, sources])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (artworkEditorOpen) setArtworkEditorOpen(false)
      else onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [artworkEditorOpen, onClose])

  return <><ModalPortal><div className="modal-backdrop content-detail-backdrop" onMouseDown={onClose}>
    <section className="modal-card content-detail-dialog" aria-modal="true" role="dialog" aria-labelledby="content-detail-title" onMouseDown={(event) => event.stopPropagation()}>
      <header className="content-detail-header">
        <div><SenderLogo sender={film.sender} compact /><span>{film.topic}</span><h2 id="content-detail-title">{film.title}</h2>
          {metadata && (metadata.releaseYear || metadata.fskRating != null || metadata.genres.length > 0) && <div className="content-detail-tags">
            {metadata.releaseYear && <span className="year">{metadata.releaseYear}</span>}
            {metadata.fskRating != null && <span>FSK {metadata.fskRating}</span>}
            {metadata.genres.map((genre) => <span key={genre}>{genre}</span>)}
          </div>}
        </div>
        <button className="icon-button" type="button" onClick={onClose} aria-label={t('common.close')}><X /></button>
      </header>

      <div className="content-detail-layout">
        <div className="content-preview-column">
          <section className={`content-player artwork-${artworkFormat} ${playerOpen ? 'active' : ''}`}>
            {!playerOpen && artworkUrl && <><img className="content-player-artwork" src={artworkUrl} alt="" onLoad={(event) => setArtworkFormat(imageFormat(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight))} /><img className="content-player-artwork-foreground" src={artworkUrl} alt="" /></>}
            {!playerOpen && metadata?.genres.length ? <div className="content-hero-genres">{metadata.genres.map((genre) => <span key={genre}>{genre}</span>)}</div> : null}
            {playerOpen && playable && selectedMediaUrl && !playerFailed
              ? <video key={selectedMediaUrl} controls autoPlay playsInline preload="metadata" onError={() => setPlayerFailed(true)}><source src={selectedMediaUrl} /></video>
              : <div className="content-player-placeholder"><MonitorPlay /><strong>{playerFailed ? t('sendungen.playerFailed') : t('sendungen.playerTitle')}</strong><p>{playable ? t('sendungen.playerHint') : t('sendungen.playerUnsupported')}</p>{playable && <button className="primary-button" type="button" onClick={() => { setPlayerFailed(false); setPlayerOpen(true) }}><Play />{t('sendungen.startPreview')}</button>}</div>}
          </section>
          {sources.length > 1 && <div className="content-source-tabs" aria-label={t('sendungen.sources')}>
            {sources.map((source) => <button type="button" className={quality === source.quality ? 'active' : ''} key={source.quality} onClick={() => { setQuality(source.quality); setPlayerOpen(false); setPlayerFailed(false) }}><strong>{t(`downloadDialog.${source.quality.toLowerCase()}`)}</strong><small>{source.resolution ?? t('sendungen.resolutionNotProvided')}</small></button>)}
          </div>}
          <div className="content-detail-primary-actions">
            <button className={`primary-button ${libraryStatus?.downloaded ? 'download-present' : ''}`} type="button" title={downloadTitle} aria-label={`${downloadTitle}: ${film.title}`} disabled={downloadDisabled} onClick={onDownload}><Download />{t('sendungen.download')}</button>
            {playerOpen && <button className="quiet-button" type="button" onClick={() => setPlayerOpen(false)}><Pause />{t('sendungen.stopPreview')}</button>}
            {websiteUrl && <a className="quiet-button" href={websiteUrl} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer"><ExternalLink />{t('sendungen.website')}</a>}
            {artworkEditable && <button className="quiet-button" type="button" onClick={() => setArtworkEditorOpen(true)}><ImagePlus />{t('newMovies.chooseArtwork')}</button>}
          </div>
        </div>

        <div className="content-metadata-column">
          {libraryStatus?.downloaded && <section className="content-library-status">
            <div className="content-library-status-title"><CircleCheck /><div><span>{t('newMovies.libraryKicker')}</span><strong>{t('newMovies.libraryTitle')}</strong><p>{t('newMovies.libraryProfiles', { profiles: libraryProfiles.join(', ') })}</p></div></div>
            <div className="content-library-copies">
              {libraryStatus.copies.map((copy) => <div key={`${copy.mediaRootId}:${copy.relativePath}:${copy.downloadProfileId ?? ''}`}>
                <div><strong>{copy.downloadProfileName || t('newMovies.earlierDownload')}</strong>{!copy.downloadProfileActive && copy.downloadProfileName && <span>{t('newMovies.inactiveProfile')}</span>}</div>
                <code>{copy.mediaRootLabel} / {copy.relativePath}</code>
                <small>{formatBytes(copy.sizeBytes)} · {new Date(copy.downloadedAt).toLocaleDateString('de-DE')} · {t(copy.detection === 'TRACKED_DOWNLOAD' ? 'newMovies.trackedDownload' : 'newMovies.profileMatch')}</small>
              </div>)}
            </div>
          </section>}
          {metadataEnrichment && <section className={`metadata-enrichment-status status-${metadataEnrichment.status.toLowerCase()}`}>
            <div>{metadataRetrying ? <LoaderCircle className="spin" /> : <RotateCcw />}<span><strong>{enrichmentLabel}</strong>
              <small>{metadataEnrichment.retryAfter && metadataEnrichment.status !== 'PENDING'
                ? t('newMovies.metadataRetryAt', { date: new Date(metadataEnrichment.retryAfter).toLocaleString('de-DE') })
                : t(`newMovies.metadataStateHints.${metadataEnrichment.status}`)}</small></span></div>
            {onMetadataRetry && (metadataEnrichment.status !== 'MATCHED' || !metadata?.artworkAvailable) && <button className="quiet-button" type="button" disabled={metadataRetrying} onClick={onMetadataRetry}><RotateCcw />{t('newMovies.retryMetadata')}</button>}
            {metadataEnrichment.detail && metadataEnrichment.status !== 'MATCHED' && <details><summary>{t('newMovies.metadataTechnicalDetail')}</summary><small>{metadataEnrichment.detail}</small></details>}
            {metadataRetryError && <div className="inline-error">{t('newMovies.metadataRetryFailed')}</div>}
          </section>}
          {metadata && <section className="external-metadata">
            {metadata.provider !== 'MANUAL' && <>
              <span className="metadata-kicker">{t('newMovies.filmMetadata')}</span>
              <div className="metadata-title"><strong>{metadata.title}{metadata.releaseYear ? ` (${metadata.releaseYear})` : ''}</strong>{metadata.originalTitle && metadata.originalTitle !== metadata.title && <span>{metadata.originalTitle}</span>}</div>
              {metadata.genres.length > 0 && <div className="metadata-genres">{metadata.genres.map((genre) => <span key={genre}>{genre}</span>)}</div>}
              {(metadata.fskRating != null || metadata.directors.length > 0 || metadata.countries.length > 0 || metadata.languages.length > 0) && <dl className="metadata-facts">
                {metadata.fskRating != null && <><dt>{t('newMovies.ageRating')}</dt><dd>FSK {metadata.fskRating}</dd></>}
                {metadata.directors.length > 0 && <><dt>{t('newMovies.directors')}</dt><dd>{metadata.directors.join(', ')}</dd></>}
                {metadata.countries.length > 0 && <><dt>{t('newMovies.countries')}</dt><dd>{metadata.countries.join(', ')}</dd></>}
                {metadata.languages.length > 0 && <><dt>{t('newMovies.languages')}</dt><dd>{metadata.languages.join(', ')}</dd></>}
              </dl>}
              {metadata.overview && <p className="metadata-overview">{metadata.overview}</p>}
            </>}
            <details className="metadata-sources" open={metadata.provider === 'MANUAL'}><summary>{t('newMovies.sourcesAndCredits')}</summary>
            {metadata.provider !== 'MANUAL' && <div className="metadata-links">{metadataProviderLabel && (metadataUrl ? <a href={metadataUrl} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">{metadataProviderLabel}</a> : <span>{metadataProviderLabel}</span>)}{imdbUrl && <a href={imdbUrl} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">IMDb</a>}<small>{t('newMovies.metadataMatch', { confidence: metadata.matchConfidence })}</small></div>}
            {metadata.attribution && <small className="metadata-attribution">{metadata.attribution}</small>}
            {(artworkSourceUrl || metadata.artworkCreator || metadata.artworkLicense) && <small className="metadata-artwork-credit">
              {t('newMovies.artwork')}: {metadata.artworkCreator && <span>{metadata.artworkCreator} · </span>}
              {artworkLicenseUrl ? <a href={artworkLicenseUrl} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">{metadata.artworkLicense || t('newMovies.license')}</a> : metadata.artworkLicense}
              {artworkSourceUrl && <>{(metadata.artworkLicense || metadata.artworkCreator) && ' · '}<a href={artworkSourceUrl} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">{t('newMovies.imageSource')}</a></>}
            </small>}
            </details>
          </section>}
          <div className="classification-details">
            <ContentTypeSelect film={film} disabled={classificationUpdating} onChange={onClassificationChange} />
            <div><strong>{film.classification.source === 'MANUAL' ? t('sendungen.classification.corrected') : t('sendungen.classification.automatic')}</strong><span>{t('sendungen.classification.confidence', { confidence: film.classification.confidence })}</span></div>
            {film.classification.evidence.length > 0 && <small>{film.classification.evidence.map((reason) => t(`sendungen.classification.evidence.${reason}`, { defaultValue: reason })).join(' · ')}</small>}
          </div>
          <div className="meta-row">
            <span><CalendarDays />{film.broadcastAt ? new Date(film.broadcastAt).toLocaleString('de-DE') : '—'}</span>
            <span><Clock3 />{formatDuration(film.durationSeconds)}</span>
            {production && <span title={t('newMovies.production')}><Clapperboard />{production}</span>}
          </div>
          <div className="details-facts">
            <span><b>{t('sendungen.size')}</b>{film.sizeMb == null ? '—' : `${film.sizeMb.toLocaleString('de-DE')} MB`}</span>
            <span><b>{t('sendungen.geo')}</b>{film.geo || t('sendungen.noRestriction')}</span>
            <span><b>{t('sendungen.sources')}</b>{sources.length}</span>
          </div>
          <h3>{t('sendungen.description')}</h3><p className="description">{film.description || '—'}</p>
          <details className="raw-details"><summary><FileJson />{t('sendungen.rawDetails')}</summary><pre>{JSON.stringify({ film, metadata, metadataEnrichment, library: libraryStatus }, null, 2)}</pre></details>
          {stateError && <div className="inline-error">{t('sendungen.libraryStateError')}</div>}
          {classificationError && <div className="inline-error">{t('sendungen.classification.error')}</div>}
          {(onSubscribe || onBookmark || onSeen) && <div className="content-library-actions">
            {onSubscribe && <button className="quiet-button" type="button" onClick={onSubscribe}><RadioTower />{t('sendungen.createSubscription')}</button>}
            {onBookmark && <button className={`quiet-button library-toggle ${film.bookmarked ? 'active' : ''}`} type="button" aria-pressed={film.bookmarked} disabled={stateUpdating} onClick={onBookmark}><Bookmark />{film.bookmarked ? t('sendungen.removeBookmark') : t('sendungen.addBookmark')}</button>}
            {onSeen && <button className={`quiet-button library-toggle ${film.seen ? 'active' : ''}`} type="button" aria-pressed={film.seen} disabled={stateUpdating} onClick={onSeen}><Check />{film.seen ? t('sendungen.markUnseen') : t('sendungen.markSeen')}</button>}
          </div>}
        </div>
      </div>
    </section>
  </div></ModalPortal>
  {artworkEditorOpen && artworkEventId && onArtworkUrl && onArtworkUpload && <ArtworkWizard
    eventId={artworkEventId}
    initialQuery={artworkSearchQuery}
    hasManualArtwork={Boolean(metadata?.artworkManual)}
    externalError={artworkError}
    onSelect={onArtworkUrl}
    onUpload={onArtworkUpload}
    onRemove={onArtworkRemove}
    onClose={() => setArtworkEditorOpen(false)}
  />}
  </>
}

function isBrowserPlayable(value: string) {
  try {
    const url = new URL(value)
    const path = url.pathname.toLowerCase()
    if (path.endsWith('.m3u8')) {
      return document.createElement('video').canPlayType('application/vnd.apple.mpegurl') !== ''
    }
    if (path.endsWith('.mpd') || path.endsWith('.m3u')) return false
    return url.protocol === 'https:' || url.protocol === 'http:'
  } catch {
    return false
  }
}

function imageFormat(width: number, height: number): 'landscape' | 'poster' | 'square' {
  if (width <= 0 || height <= 0) return 'landscape'
  const ratio = width / height
  if (ratio < .86) return 'poster'
  if (ratio < 1.18) return 'square'
  return 'landscape'
}
